import React, { useState, useEffect, useRef } from 'react';
import './GettingStartedPanel.css';

type Tab = 'profiles' | 'contacts' | 'funeral' | 'documents' | 'financial' | 'heirlooms' | 'notes' | 'letters' | 'chat';

interface Props {
  vaultId: string;
  memberCount: number;
  hasContent: boolean;
  onTabSelect: (tab: Tab) => void;
  onComplete?: () => void;
}

const DISMISS_KEY_PREFIX = 'cv_gs_dismissed_';
const CHECKED_KEY_PREFIX = 'cv_gs_checked_';

interface Step {
  id: string;
  tab: Tab;
  title: string;
  description: string;
  done: boolean;
}

export default function GettingStartedPanel({ vaultId, memberCount, hasContent, onTabSelect, onComplete }: Props) {
  const [dismissed, setDismissed] = useState(() => {
    const key = DISMISS_KEY_PREFIX + vaultId;
    if (localStorage.getItem(key)) return true;
    // Content gate: skip onboarding for existing users who haven't started the checklist.
    // If they've already checked any steps, they're mid-onboarding — let them finish.
    const hasStarted = !!localStorage.getItem(CHECKED_KEY_PREFIX + vaultId);
    if (hasContent && !hasStarted) {
      localStorage.setItem(key, '1');
      return true;
    }
    return false;
  });

  const [checked, setChecked] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(CHECKED_KEY_PREFIX + vaultId);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  function toggleStep(id: string) {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      localStorage.setItem(CHECKED_KEY_PREFIX + vaultId, JSON.stringify(Array.from(next)));
      return next;
    });
  }

  const steps: Step[] = [
    {
      id: 'accounts',
      tab: 'financial',
      title: 'Add your important accounts',
      description: 'Phone PIN, computer password, bank accounts — everything your family needs in the first 48 hours.',
      done: checked.has('accounts'),
    },
    {
      id: 'letter',
      tab: 'letters',
      title: 'Leave a Legacy Message',
      description: 'Write a sealed letter or record a video — your face, your voice, your words. Five minutes that will save your family hours of searching and a lifetime of wondering.',
      done: checked.has('letter'),
    },
    {
      id: 'document',
      tab: 'documents',
      title: 'Upload one important document',
      description: 'Your will, an insurance policy, or a birth certificate — the one your family will need most.',
      done: checked.has('document'),
    },
    {
      id: 'invite',
      tab: 'profiles',
      title: 'Invite someone you trust',
      description: 'Your spouse, a child, or a close friend. The moment you invite someone, the vault becomes real.',
      done: memberCount > 1 || checked.has('invite'),
    },
  ];

  const completedCount = steps.filter(s => s.done).length;
  const allDone = completedCount === steps.length;

  const prevAllDoneRef = useRef(false);
  useEffect(() => {
    if (allDone && !prevAllDoneRef.current) {
      setTimeout(() => onComplete?.(), 400);
    }
    prevAllDoneRef.current = allDone;
  }, [allDone]);

  if (dismissed || allDone) return null;

  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY_PREFIX + vaultId, '1');
    setDismissed(true);
  }

  return (
    <div className="gs-panel">
      <div className="gs-header">
        <div className="gs-header-left">
          <span className="gs-badge">Getting Started</span>
          <span className="gs-progress">{completedCount} of {steps.length} complete</span>
        </div>
        <button className="gs-dismiss" onClick={handleDismiss} aria-label="Dismiss getting started panel">
          ✕
        </button>
      </div>

      <p className="gs-tagline">Be kind to your loved ones — four steps that make all the difference.</p>

      <div className="gs-progress-bar">
        <div
          className="gs-progress-fill"
          style={{ width: `${(completedCount / steps.length) * 100}%` }}
        />
      </div>

      <ul className="gs-steps">
        {steps.map(step => (
          <li key={step.id} className={`gs-step${step.done ? ' gs-step--done' : ''}`}>
            <button
              className="gs-step-check"
              onClick={() => toggleStep(step.id)}
              aria-label={step.done ? `Mark "${step.title}" incomplete` : `Mark "${step.title}" complete`}
              title={step.done ? 'Click to uncheck' : 'Click to mark done'}
            >
              {step.done ? '✓' : '○'}
            </button>
            <div className="gs-step-body">
              <div className="gs-step-title">{step.title}</div>
              <div className="gs-step-desc">{step.description}</div>
            </div>
            {!step.done && (
              <button
                className="gs-step-action"
                onClick={() => {
                  toggleStep(step.id);
                  onTabSelect(step.tab);
                  setTimeout(() => {
                    document.querySelector('.vault-detail-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 50);
                }}
              >
                Start →
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
